import React from 'react';
import { Helmet } from 'react-helmet-async';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import PText from '../components/PText';
import AboutImg from '../assets/images/ninja.png';
import AboutInfoTech from '../components/AboutInfoTech';
import AboutInfoItem from '../components/AboutInfoItem';
import ContactBanner from '../components/ContactBanner';

const AboutPageStyles = styled.div`
  padding: 20rem 0 10rem 0;

  .top-section {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 2rem;
  }
  .intro-left {
    flex: 5;
  }
  .right {
    flex: 2;
    img {
      border: 2px solid var(--gray-1);
      border-radius: 16px;
      width: 100%;
    }
  }
  .about__subheading {
    font-size: 2.2rem;
    margin-bottom: 2rem;
    span {
      background-color: var(--deep-dark);
      padding: 0.5rem;
      border-radius: 8px;
    }
  }
  .about__heading {
    font-size: 3.6rem;
    margin-bottom: 3rem;
  }
  .about__info {
    margin-bottom: 3rem;
    .para {
      max-width: 100%;
      text-align: justify;
      padding-right: 20px;
    }
  }

  /* Quick stats bar */
  .stats {
    display: flex;
    gap: 4rem;
    margin: 3rem 0 4rem;
    .stat {
      display: flex;
      flex-direction: column;
      gap: 0.4rem;
    }
    .stat__number {
      font-size: 3.6rem;
      font-family: 'Montserrat Bold';
      color: var(--highlight-blue);
      line-height: 1;
    }
    .stat__label {
      font-size: 1.3rem;
      color: var(--gray-1);
      opacity: 0.65;
      text-transform: uppercase;
      letter-spacing: 0.1rem;
    }
  }

  .btn-theme {
    display: inline-block;
    font-size: 1.8rem;
    padding: 0.7em 2em;
    border: 2px solid var(--highlight-blue);
    border-radius: 8px;
    color: var(--highlight-blue);
    background-color: transparent;
    cursor: pointer;
    transition: background-color 0.2s ease, color 0.2s ease;
    &:hover {
      background-color: var(--highlight-blue);
      color: var(--dark-bg);
    }
  }

  /* Section info blocks */
  .about__info__items {
    margin-top: 10rem;
  }
  .about__info__item {
    margin-bottom: 8rem;
    padding-top: 6rem;
    border-top: 1px solid var(--gray-2);
    &:first-child {
      border-top: none;
      padding-top: 0;
    }
  }
  .about__info__heading {
    font-size: 3.6rem;
    text-transform: uppercase;
    margin-bottom: 1rem;
  }

  /* 2-column skills grid */
  .skills-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem 5rem;
  }

  @media only screen and (max-width: 768px) {
    padding: 10rem 0;
    .top-section {
      flex-direction: column;
      gap: 5rem;
    }
    .about__subheading {
      font-size: 2.5rem;
    }
    .about__heading {
      font-size: 2.5rem;
    }
    .about__info__heading {
      font-size: 3rem;
    }
    .about-display-image {
      display: none;
    }
    .stats {
      gap: 2.5rem;
      .stat__number {
        font-size: 2.8rem;
      }
    }
    .skills-grid {
      grid-template-columns: 1fr;
    }
  }
`;

export default function About() {
  return (
    <>
      <Helmet><title>About | Anas Qazi</title></Helmet>
      <AboutPageStyles>
        <div className="container">

          {/* Top section — intro + image */}
          <div className="top-section">
            <div className="intro-left">
              <p className="about__subheading">
                Hi, I am <span>Anas Qazi</span>
              </p>
              <h2 className="about__heading">A Software Engineer</h2>
              <div className="about__info">
                <PText>
                  Senior Full-Stack Engineer with 10+ years of experience delivering scalable,
                  secure, enterprise-grade platforms across fintech, banking, and cloud environments.
                  At Veriday, I lead end-to-end development of Liferay DXP portals for major clients
                  including CIBC, Canadian Tire, and Laurentian Bank — owning architecture, API
                  integrations, CI/CD pipelines, AWS infrastructure, and WCAG&nbsp;2.1/2.2 compliance.
                </PText>
                <PText>
                  My background spans Oracle consulting and database administration through to
                  full-stack engineering, giving me a rare cross-domain perspective that bridges
                  deep technical expertise with real business impact.
                </PText>
                <PText>
                  Passionate about mentorship, I have taught 200+ students at the University of
                  Toronto and guided aspiring engineers 1:1 at Springboard.
                </PText>
              </div>

              {/* Quick stats */}
              <div className="stats">
                <div className="stat">
                  <span className="stat__number">10+</span>
                  <span className="stat__label">Years Experience</span>
                </div>
                <div className="stat">
                  <span className="stat__number">7+</span>
                  <span className="stat__label">Companies</span>
                </div>
                <div className="stat">
                  <span className="stat__number">3</span>
                  <span className="stat__label">Countries</span>
                </div>
              </div>

              <Link to="/resume" className="btn-theme">Resume</Link>
            </div>

            <div className="right about-display-image">
              <img src={AboutImg} alt="Anas Qazi" />
            </div>
          </div>

          {/* Info sections */}
          <div className="about__info__items">

            {/* Education */}
            <div className="about__info__item">
              <h2 className="about__info__heading">Education</h2>
              <AboutInfoItem
                title="Full Stack Web Development"
                items={['2020 — University of Toronto, Canada']}
              />
              <AboutInfoItem
                title="Bachelor in Computer Engineering"
                items={['2013 — University of Mumbai, India']}
              />
            </div>

            {/* Skills */}
            <div className="about__info__item">
              <h2 className="about__info__heading">My Skills</h2>
              <div className="skills-grid">
                <AboutInfoTech
                  title="Languages"
                  items={['JavaScript (ES6+)', 'HTML', 'CSS']}
                />
                <AboutInfoTech
                  title="Frameworks"
                  items={['React', 'Angular', 'Vue', 'Next.js', 'Flask', 'Django']}
                />
                <AboutInfoTech
                  title="Backend & Libraries"
                  items={['Express.js', 'Node.js', 'GraphQL', 'Redux', 'jQuery', 'Bootstrap', 'Materialize', 'Axios', 'AJAX', 'Asyncio']}
                />
                <AboutInfoTech
                  title="Cloud — AWS"
                  items={['EC2', 'Fargate', 'ECS', 'Lambda', 'S3', 'RDS', 'Aurora', 'ElastiCache', 'VPC', 'ELB', 'IAM', 'SNS', 'SQS', 'CloudFormation', 'CodePipeline', 'CodeDeploy', 'CodeBuild']}
                />
                <AboutInfoTech
                  title="Databases"
                  items={['MySQL', 'PostgreSQL', 'SQLite', 'SQL Server', 'MongoDB', 'Sequelize', 'Mongoose']}
                />
                <AboutInfoTech
                  title="DevOps & CI/CD"
                  items={['Docker', 'Kubernetes', 'Jenkins', 'GitHub Actions', 'GitLab CI', 'Travis CI', 'CircleCI', 'Webpack', 'Heroku']}
                />
                <AboutInfoTech
                  title="Version Control"
                  items={['Git', 'GitHub', 'Bitbucket', 'NPM', 'YARN', 'Pip', 'pipenv']}
                />
                <AboutInfoTech
                  title="Operating Systems"
                  items={['Linux (Ubuntu, CentOS)', 'Unix', 'HP-UX', 'Solaris', 'macOS', 'Windows']}
                />
                <AboutInfoTech
                  title="Server Management"
                  items={['Apache', 'Nginx', 'Tomcat']}
                />
                <AboutInfoTech
                  title="Oracle"
                  items={['Real Application Cluster', 'Data Guard', 'E-Business Suite', 'Enterprise Manager', 'ASM', 'SAN', 'RMAN']}
                />
                <AboutInfoTech
                  title="Liferay"
                  items={['Portal', 'Liferay DXP', 'Themes', 'Service Builder', 'APIs', 'Analytics Cloud', 'Authentication', 'IDE', 'Developer Studio', 'Workspace']}
                />
                <AboutInfoTech
                  title="Tools"
                  items={['Postman', 'Swagger']}
                />
              </div>
            </div>

            {/* Professional Experience */}
            <div className="about__info__item">
              <h2 className="about__info__heading">Professional Experience</h2>
              <AboutInfoItem
                title="Software Engineer @ Veriday"
                span="Sept 2021 – Present"
                items={[]}
              />
              <AboutInfoItem
                title="Instructor @ University of Toronto"
                span="May 2020 – Apr 2022 · 2 yrs"
                items={[]}
              />
              <AboutInfoItem
                title="Software Engineering Mentor @ Springboard"
                span="May 2020 – Jul 2023 · 3 yrs 3 mo"
                items={[]}
              />
              <AboutInfoItem
                title="Software Developer @ Myplanet"
                span="May 2021 – Sept 2021 · 5 mo"
                items={[]}
              />
              <AboutInfoItem
                title="Software Engineer @ Finneo"
                span="June 2020 – May 2021 · 11 mo"
                items={[]}
              />
              <AboutInfoItem
                title="Database Administrator @ Bank Muscat"
                span="Jan 2017 – Mar 2019 · 2 yrs 3 mo"
                items={[]}
              />
              <AboutInfoItem
                title="Oracle Consultant @ Progress Ways"
                span="Jul 2014 – Jan 2017 · 2 yrs 7 mo"
                items={[]}
              />
            </div>

          </div>
        </div>
        <ContactBanner />
      </AboutPageStyles>
    </>
  );
}
